import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_TAGS } from '../../graphql/queries/tags';
import {
  CREATE_TAG, UPDATE_TAG, DELETE_TAG, ASSIGN_TAGS, REMOVE_TAGS, ASSIGN_STOCK_TAGS, REMOVE_STOCK_TAGS,
} from '../../graphql/mutations/tags';
import { toast } from 'react-hot-toast';

const DEFAULT_COLORS = [
  '#4f46e5', '#059669', '#d97706', '#dc2626', '#7c3aed',
  '#0891b2', '#be185d', '#65a30d', '#ea580c', '#4338ca'
];

const TagModal = ({ isOpen, onClose, positionId, currentTags = [], onTagsChanged, isStock = false }) => {
  const [search, setSearch] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState(new Set());
  const [editingTag, setEditingTag] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const searchRef = useRef(null);
  const initialTagIds = useRef(new Set());

  const { data: tagsData } = useQuery(GET_TAGS, { fetchPolicy: 'cache-and-network', skip: !isOpen });
  const allTags = tagsData?.tags || [];

  const refetchOpts = { refetchQueries: [{ query: GET_TAGS }] };
  const [createTag] = useMutation(CREATE_TAG, refetchOpts);
  const [updateTag] = useMutation(UPDATE_TAG, refetchOpts);
  const [deleteTag] = useMutation(DELETE_TAG, refetchOpts);
  const [assignTags] = useMutation(isStock ? ASSIGN_STOCK_TAGS : ASSIGN_TAGS, refetchOpts);
  const [removeTags] = useMutation(isStock ? REMOVE_STOCK_TAGS : REMOVE_TAGS, refetchOpts);
  const usageCount = (tag) => (isStock ? tag.stockPositionCount : tag.positionCount) ?? 0;
  const totalUsage = (tag) => (tag.positionCount ?? 0) + (tag.stockPositionCount ?? 0);

  useEffect(() => {
    if (isOpen) {
      const ids = new Set(currentTags.map(t => t.id));
      setSelectedTagIds(ids);
      initialTagIds.current = new Set(ids);
      setSearch('');
      setEditingTag(null);
      setConfirmDeleteId(null);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen, currentTags]);

  const handleSave = useCallback(async () => {
    const initial = initialTagIds.current;
    const toAssign = [...selectedTagIds].filter(id => !initial.has(id));
    const toRemove = [...initial].filter(id => !selectedTagIds.has(id));

    try {
      if (toAssign.length > 0) {
        await assignTags({ variables: { input: { positionId, tagIds: toAssign } } });
      }
      if (toRemove.length > 0) {
        await removeTags({ variables: { input: { positionId, tagIds: toRemove } } });
      }
      if (onTagsChanged && (toAssign.length > 0 || toRemove.length > 0)) {
        onTagsChanged();
      }
      onClose();
    } catch (err) {
      toast.error('Failed to update tags');
    }
  }, [selectedTagIds, positionId, assignTags, removeTags, onTagsChanged, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (editingTag) {
          setEditingTag(null);
        } else if (confirmDeleteId) {
          setConfirmDeleteId(null);
        } else {
          onClose();
        }
      }
      if (e.key === 'Enter' && !editingTag && !confirmDeleteId && e.target.tagName !== 'INPUT') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, editingTag, confirmDeleteId, onClose, handleSave]);

  const toggleTag = (tagId) => {
    setSelectedTagIds(prev => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  };

  const handleCreate = async () => {
    const name = search.trim();
    if (!name) return;
    try {
      const color = DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)];
      const res = await createTag({ variables: { input: { name, color } } });
      const newTag = res.data?.createTag;
      if (newTag) {
        setSelectedTagIds(prev => new Set(prev).add(newTag.id));
      }
      setSearch('');
      toast.success(`Created "${name}"`);
    } catch (err) {
      toast.error(err.message || 'Failed to create tag');
    }
  };

  const startEdit = (tag, e) => {
    e.stopPropagation();
    setEditingTag(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color || DEFAULT_COLORS[0]);
    setConfirmDeleteId(null);
  };

  const saveEdit = async () => {
    if (!editingTag || !editName.trim()) return;
    try {
      await updateTag({ variables: { input: { id: editingTag, name: editName.trim(), color: editColor } } });
      toast.success('Tag updated');
      setEditingTag(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update tag');
    }
  };

  const handleDelete = async (tagId) => {
    try {
      await deleteTag({ variables: { id: tagId } });
      setSelectedTagIds(prev => {
        const next = new Set(prev);
        next.delete(tagId);
        return next;
      });
      initialTagIds.current.delete(tagId);
      if (onTagsChanged) {
        onTagsChanged();
      }
      setConfirmDeleteId(null);
      toast.success('Tag deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete tag');
    }
  };

  const filtered = allTags.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.slug.toLowerCase().includes(search.toLowerCase())
  );

  const canCreate = search.trim().length > 0 &&
    !allTags.some(t => t.name.toLowerCase() === search.trim().toLowerCase());

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-5 pt-5 pb-3">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Manage Tags</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 pb-2">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (canCreate && filtered.length === 0) {
                  handleCreate();
                } else {
                  handleSave();
                }
              }
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="px-5 pb-3 max-h-72 overflow-y-auto">
          {filtered.map(tag => (
            <div key={tag.id}>
              {editingTag === tag.id ? (
                <div className="py-2 px-2 mb-1 rounded bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                        if (e.key === 'Escape') { e.stopPropagation(); setEditingTag(null); }
                      }}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      autoFocus
                    />
                    <input
                      type="color"
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      className="w-7 h-7 rounded border border-gray-300 dark:border-gray-600 cursor-pointer p-0"
                      title="Tag color"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      className="px-2.5 py-1 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingTag(null)}
                      className="px-2.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`flex items-center justify-between py-2 px-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    selectedTagIds.has(tag.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                  onClick={() => toggleTag(tag.id)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                        selectedTagIds.has(tag.id)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300 dark:border-gray-500'
                      }`}
                    >
                      {selectedTagIds.has(tag.id) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color || '#6b7280' }}
                    />
                    <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{tag.name}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                      ({usageCount(tag)})
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <button
                      onClick={(e) => startEdit(tag, e)}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                      title="Edit tag"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    {confirmDeleteId === tag.id ? (
                      <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] text-red-600 whitespace-nowrap">Delete from {totalUsage(tag)} pos?</span>
                        <button
                          onClick={() => handleDelete(tag.id)}
                          className="text-[10px] text-red-600 hover:text-red-700 font-medium"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-[10px] text-gray-500 hover:text-gray-700"
                        >
                          No
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(tag.id); }}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Delete tag"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {filtered.length === 0 && !canCreate && (
            <div className="py-4 text-center text-sm text-gray-400">No tags found</div>
          )}

          {canCreate && (
            <button
              onClick={handleCreate}
              className="w-full text-left py-2 px-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              + Create &ldquo;{search.trim()}&rdquo;
            </button>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 pb-5 pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagModal;
